import { useEffect, useRef } from 'react';

import useModal from '@/hooks/useModal';
import { MODAL_BUTTON_TYPE, MODAL_TYPE } from '@/types/modal';

interface UseRefreshPreventionOptions {
  enabled?: boolean;
  modalTitle?: string;
  modalMessage?: string;
  skipReload?: boolean;
  onCancelText?: string;
  onConfirmText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
}

const useRefreshPrevention = (options: UseRefreshPreventionOptions = {}) => {
  const {
    enabled = true,
    modalTitle = '페이지를 새로고침하시겠어요?',
    modalMessage = '작성 중인 내용이 사라질 수 있습니다.',
    skipReload = false,
    onCancelText = '취소',
    onConfirmText = '새로고침',
    onConfirm,
    onCancel,
  } = options;

  const { showModal, closeModal } = useModal();
  const shouldPreventRef = useRef(false);
  const enabledRef = useRef(enabled);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const isNormalRefresh =
        event.key === 'F5' ||
        ((event.ctrlKey || event.metaKey) && event.key === 'r') ||
        ((event.ctrlKey || event.metaKey) && event.key === 'R');

      const isHardRefresh =
        (event.key === 'F5' && event.shiftKey) ||
        ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'r') ||
        ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'R');

      const isRefreshKey = isNormalRefresh || isHardRefresh;

      if (isRefreshKey) {
        event.preventDefault();
        shouldPreventRef.current = true;

        showModal({
          type: MODAL_TYPE.ALERT,
          props: {
            title: modalTitle,
            message: modalMessage,
            buttons: [
              {
                type: MODAL_BUTTON_TYPE.DEFAULT,
                label: onCancelText,
                onClick: () => {
                  closeModal();
                  shouldPreventRef.current = false;
                  onCancel?.();
                },
              },
              {
                type: MODAL_BUTTON_TYPE.PRIMARY,
                label: onConfirmText,
                onClick: () => {
                  closeModal();
                  shouldPreventRef.current = false;
                  onConfirm?.();
                  if (!skipReload) {
                    window.location.reload();
                  }
                },
              },
            ],
          },
        });
      }
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (enabledRef.current) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled, modalTitle, modalMessage, showModal, closeModal, onConfirm, onCancel]);

  return {
    shouldPrevent: shouldPreventRef.current,
  };
};

export default useRefreshPrevention;
